
'use client';

import { useState } from 'react';
import { parseVehicleData } from '@/utils/parseVehicleData';
import { CleanVehicleData } from '@/utils/cleanVehicleObject';

import GroupedEquipment from '../../components/dataone/GroupedEquipment';
import VehicleSpecs from '../../components/dataone/VehicleSpecs';
import ColorsSection from '../../components/dataone/ColorsSection';
import SafetySection from '../../components/dataone/SafetySection';
import OEMOptionsTable from '../../components/dataone/OEMOptionsTable';
import WarrantiesTable from '../../components/dataone/WarrantiesTable';
import FuelEfficiencyCard from '../../components/dataone/FuelEfficiencyCard';
import AdasSection from '../../components/dataone/AdasSection';
import EvoxImages from '../../components/dataone/EvoxImages';
import MatchAccuracyBadge from '../../components/dataone/MatchAccuracyBadge';

// Updated VIN Test Component - Jan 2025
export default function VinTestPage() {
  const [vin, setVin] = useState('');
  const [allStyles, setAllStyles] = useState<CleanVehicleData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    package_code: '',
    drive_type: '',
    vehicle_type: '',
    body_type: '',
    body_subtype: '',
    doors: '',
    bedlength: '',
    wheelbase: '',
    msrp: '',
    invoice_price: ''
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      const response = await fetch("https://xmfobwtdgbqcaorfcffi.supabase.co/functions/v1/dataone-vin-lookup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhtZm9id3RkZ2JxY2FvcmZjZmZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzY4ODczODksImV4cCI6MjA1MjQ2MzM4OX0.1c4VfH4nOnvEwixPUGGhz-skesHJz8j1ZaAG1ipkm8s"
        },
        body: JSON.stringify({ vin, ...filters })
      });
  
      const json = await response.json();
      if (!response.ok || !json?.data) throw new Error(json?.error || 'Failed to fetch');
  
      const rawStyles = json.data?.query_responses?.['Request-Sample']?.us_market_data?.us_styles || [];
      const parsedStyles = rawStyles.map((_, index) => parseVehicleData(json.data, index));
      setAllStyles(parsedStyles);
      setSelectedIndex(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };
  

  const parsedData = allStyles[selectedIndex];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold">VIN Lookup (Test Page)</h1>
      <form onSubmit={handleSubmit} className="flex gap-4 items-center">
        <input
          type="text"
          value={vin}
          onChange={(e) => setVin(e.target.value)}
          placeholder="Enter VIN"
          className="border rounded px-4 py-2 w-full"
        />
        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
          Lookup
        </button>
      </form>
      <button
  type="button"
  onClick={() => setShowAdvanced(!showAdvanced)}
  className="text-blue-600 underline text-sm"
>
  {showAdvanced ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
</button>

{showAdvanced && (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
    {Object.entries(filters).map(([key, val]) => (
      <div key={key}>
        <label className="block text-sm font-medium capitalize mb-1">{key.replace(/_/g, ' ')}</label>
        <input
          name={key}
          value={val}
          onChange={handleInputChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
    ))}
  </div>
)}


      {loading && <p>Loading...</p>}
      {error && <p className="text-red-600">❌ {error}</p>}

      {allStyles.length > 0 && (
        <div className="mt-4">
          <label className="font-medium text-sm">Select Style:</label>
          <select
            className="ml-2 border px-2 py-1 rounded"
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
          >
            {allStyles.map((style, idx) => (
              <option key={idx} value={idx}>
                {style.basicData.year} {style.basicData.make} {style.basicData.model} {style.basicData.trim || `Style ${idx + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {parsedData && (
        <div className="mt-6 space-y-4">
          <h2 className="text-xl font-semibold mb-2">Basic Info</h2>
          <ul className="space-y-1 text-sm text-gray-700">
            {Object.entries(parsedData.basicData)
              .filter(([_, value]) => value && value !== "")
              .map(([key, value]) => (
                <li key={key}>
                  <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {value}
                </li>
              ))}
          </ul>

          <MatchAccuracyBadge
            matchedCount={parsedData.matchAccuracy.matched}
            totalCount={parsedData.matchAccuracy.total}
            matchedAngles={Object.keys(parsedData.matchAccuracy.mapping).filter(
              (key) => parsedData.matchAccuracy.mapping[key] === 'Y'
            )}
          />

          <AdasSection adasSystems={parsedData.advanced_driver_assistance_systems || []} />
          <EvoxImages images={parsedData.evoxImages || []} />
          <VehicleSpecs specs={parsedData.specs} />
          <ColorsSection
            exterior={parsedData.colors?.exterior || []}
            interior={parsedData.colors?.interior || []}
          />
          <SafetySection safetyEquipment={parsedData.safetyEquipment || {}} />
          {parsedData.oemOptions?.length > 0 && (
            <OEMOptionsTable options={parsedData.oemOptions} />
          )}
          {parsedData.warranties?.length > 0 && (
            <WarrantiesTable warranties={parsedData.warranties} />
          )}
          {parsedData.epaFuelEfficiency?.length > 0 && (
            <FuelEfficiencyCard epaFuelEfficiency={parsedData.epaFuelEfficiency} />
          )}
          <GroupedEquipment equipment={parsedData.equipment} />
        </div>
      )}
    </div>
  );
}
